import {
  Button,
  Card,
  Empty,
  Flex,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Space,
  Switch,
  Typography
} from 'antd'
import { KeyRound } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { OrganizationAuthMethodEnum } from 'src/api/main'
import { QueryErrorAlert } from 'src/components'
import {
  type ConnectedAccountItem,
  useConnectedAccounts,
  useMfaSettings,
  useSetPasswordModal,
  useSignInPolicySettings
} from 'src/features/account/hooks'
import {
  AppleBrandIcon,
  GoogleBrandIcon
} from 'src/features/auth/components/SocialProviderIcons'
import { useSessionContext } from 'src/hooks'

const { Paragraph, Text } = Typography

const actionButtonStyle = {
  minWidth: 110,
  textAlign: 'center' as const
}

export const AccountAuthenticationPanel = () => {
  const { t } = useTranslation()
  const { user } = useSessionContext()
  const userEmail = user?.user?.email

  const connectedAccountsState = useConnectedAccounts({
    userEmail
  })
  const mfaState = useMfaSettings()
  const signInPolicyState = useSignInPolicySettings()
  const setPasswordState = useSetPasswordModal()

  const hasBlockError =
    connectedAccountsState.hasError ||
    mfaState.hasError ||
    signInPolicyState.hasError

  const refetchAll = () => {
    connectedAccountsState.refetch()
    mfaState.refetch()
    signInPolicyState.refetch()
  }

  const renderConnectedAccountAction = (item: ConnectedAccountItem) => {
    if (item.kind === 'password' && !item.linked) {
      return (
        <Button
          key="set-password"
          size="small"
          type="primary"
          style={actionButtonStyle}
          onClick={setPasswordState.openModal}
        >
          {t('account.authentication.connectedAccounts.setPassword')}
        </Button>
      )
    }

    if (item.kind === 'password') {
      return (
        <Button key="required" size="small" disabled style={actionButtonStyle}>
          {t('account.authentication.connectedAccounts.required')}
        </Button>
      )
    }

    if (item.kind === 'social' && item.linked) {
      return (
        <Button
          key="disconnect"
          size="small"
          danger
          loading={connectedAccountsState.isUnlinkPending(item.provider)}
          style={actionButtonStyle}
          onClick={() =>
            connectedAccountsState.handleUnlinkProvider(item.provider)
          }
        >
          {t('account.authentication.connectedAccounts.disconnect')}
        </Button>
      )
    }

    if (item.kind !== 'social') {
      return null
    }

    return (
      <Button
        key="connect"
        size="small"
        type="primary"
        loading={connectedAccountsState.isLinkingProvider(
          item.provider === OrganizationAuthMethodEnum.APPLE
            ? 'apple'
            : 'google'
        )}
        style={actionButtonStyle}
        onClick={
          item.provider === OrganizationAuthMethodEnum.APPLE
            ? connectedAccountsState.handleLinkApple
            : connectedAccountsState.handleLinkGoogle
        }
      >
        {t('account.authentication.connectedAccounts.link')}
      </Button>
    )
  }

  return (
    <Flex vertical gap={16}>
      {hasBlockError ? <QueryErrorAlert onRetry={refetchAll} /> : null}

      <Card
        variant="outlined"
        title={t('account.authentication.connectedAccounts.title')}
      >
        <List<ConnectedAccountItem>
          loading={connectedAccountsState.isLoading}
          dataSource={connectedAccountsState.connectedAccounts}
          locale={{
            emptyText: <Empty description={t('ui.tableEmpty')} />
          }}
          renderItem={(item) => {
            let icon: ReactNode
            let subtitle = item.email

            if (item.kind === 'password') {
              icon = <KeyRound size={18} />
              subtitle =
                item.email ||
                t('account.authentication.connectedAccounts.notLinked')
            } else {
              if (item.provider === OrganizationAuthMethodEnum.APPLE) {
                icon = <AppleBrandIcon size={18} />
              } else {
                icon = <GoogleBrandIcon size={18} />
              }

              subtitle =
                item.email ||
                t('account.authentication.connectedAccounts.notLinked')
            }

            return (
              <List.Item actions={[renderConnectedAccountAction(item)]}>
                <Space size={12} align="center">
                  {icon}
                  <Space vertical size={0}>
                    <Text strong>
                      {t(
                        `account.authentication.providers.${
                          item.kind === 'social' ? item.provider : item.kind
                        }`
                      )}
                    </Text>
                    <Text type="secondary">{subtitle}</Text>
                  </Space>
                </Space>
              </List.Item>
            )
          }}
        />
      </Card>

      <Card variant="outlined" title={t('account.authentication.mfa.title')}>
        <Flex vertical gap={16}>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t('account.authentication.mfa.description')}
          </Paragraph>

          <Flex justify="space-between" align="center">
            <Text>{t('account.authentication.mfa.toggle')}</Text>
            <Switch
              checked={mfaState.mfaConfig?.enabled ?? false}
              loading={mfaState.isLoading || mfaState.isPending}
              onChange={mfaState.handleToggle}
            />
          </Flex>
        </Flex>
      </Card>

      <Card
        variant="outlined"
        title={t('account.authentication.security.title')}
      >
        <Form layout="vertical" requiredMark={false}>
          <Flex vertical gap={16}>
            <Flex justify="space-between" align="center">
              <Text>{t('account.authentication.security.allowPassword')}</Text>
              <Switch
                checked={
                  signInPolicyState.signInPolicy?.allow_password_sign_in ??
                  false
                }
                loading={
                  signInPolicyState.isLoading || signInPolicyState.isPending
                }
                onChange={signInPolicyState.handleToggle(
                  'allow_password_sign_in'
                )}
              />
            </Flex>

            <Flex justify="space-between" align="center">
              <Text>{t('account.authentication.security.allowSocial')}</Text>
              <Switch
                checked={
                  signInPolicyState.signInPolicy?.allow_social_sign_in ?? false
                }
                loading={
                  signInPolicyState.isLoading || signInPolicyState.isPending
                }
                onChange={signInPolicyState.handleToggle(
                  'allow_social_sign_in'
                )}
              />
            </Flex>

            <Flex gap={16} wrap>
              <Form.Item
                label={t(
                  'account.authentication.security.refreshTokenLifetimeDays'
                )}
                extra={t(
                  'account.authentication.security.refreshTokenLifetimeHint',
                  'Min: 7, Max: 183'
                )}
                style={{ flex: 1, minWidth: 220, marginBottom: 0 }}
              >
                <InputNumber
                  value={
                    signInPolicyState.signInPolicy
                      ?.refresh_token_lifetime_days ?? null
                  }
                  min={7}
                  max={183}
                  disabled={signInPolicyState.isLoading}
                  onBlur={(event) => {
                    const parsed = Number(event.currentTarget.value)
                    signInPolicyState.handleNumber(
                      'refresh_token_lifetime_days'
                    )(Number.isFinite(parsed) ? parsed : null)
                  }}
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Form.Item
                label={t('account.authentication.security.trustedDeviceDays')}
                extra={t(
                  'account.authentication.security.trustedDeviceHint',
                  'Min: 1, Max: 90'
                )}
                style={{ flex: 1, minWidth: 220, marginBottom: 0 }}
              >
                <InputNumber
                  value={
                    signInPolicyState.signInPolicy
                      ?.trusted_device_validity_days ?? null
                  }
                  min={1}
                  max={90}
                  disabled={signInPolicyState.isLoading}
                  onBlur={(event) => {
                    const parsed = Number(event.currentTarget.value)
                    signInPolicyState.handleNumber(
                      'trusted_device_validity_days'
                    )(Number.isFinite(parsed) ? parsed : null)
                  }}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Flex>
          </Flex>
        </Form>
      </Card>

      <Modal
        open={setPasswordState.isOpen}
        title={t('account.authentication.setPassword.title')}
        okText={t('account.authentication.setPassword.submit')}
        cancelText={t('cancel')}
        confirmLoading={setPasswordState.isPending}
        onOk={setPasswordState.submit}
        onCancel={setPasswordState.closeModal}
        destroyOnHidden
      >
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          {t('account.authentication.setPassword.description')}
        </Paragraph>
        <Form
          form={setPasswordState.form}
          layout="vertical"
          requiredMark={false}
        >
          <Form.Item
            name="new_password"
            label={t('account.authentication.setPassword.newPassword')}
            rules={[
              {
                required: true,
                message: t(
                  'account.authentication.setPassword.passwordRequired'
                )
              },
              {
                pattern: /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+]).{10,}$/,
                message: t('password_policy')
              }
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label={t('account.authentication.setPassword.confirmPassword')}
            dependencies={['new_password']}
            rules={[
              {
                required: true,
                message: t('account.authentication.setPassword.confirmRequired')
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve()
                  }

                  return Promise.reject(
                    new Error(
                      t('account.authentication.setPassword.passwordsMustMatch')
                    )
                  )
                }
              })
            ]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </Flex>
  )
}
